import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import MKBox from "components/MKBox";
import Typography from "@mui/material/Typography";
import { settingsService } from "services/settingsService";
import CircularProgress from "@mui/material/CircularProgress";
import MdTipTapDynamicHeight from "components/MdTiptapDynamicHeight";
import Card from "@mui/material/Card";
import MKButton from "components/MKButton";
import Grid from "@mui/material/Grid";
import Fade from "@mui/material/Fade";
import MKTypography from "components/MKTypography";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Box from "@mui/material/Box";

function SettingEditorInstance({ settingKey }) {
  const [loading, setLoading] = useState(false);
  const [currentValue, setCurrentValue] = useState("");
  const [newValue, setNewValue] = useState("");
  const [description, setDescription] = useState("");

  const [alert, setAlert] = useState(null);
  const timerRef = useRef();

  const showAlert = (severity, message) => {
    setAlert({ severity, message });
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setAlert(null);
    }, 6000);
  };

  /* ---------- handlers ---------- */

  useEffect(() => {
    setLoading(true);
    settingsService.getSetting(settingKey).then((setting) => {
      setCurrentValue(setting.setting);
      setNewValue(setting.setting);
      setDescription(setting.description);
      setLoading(false);
    });
  }, [settingKey]);

  useEffect(() => {
    clearTimeout(timerRef.current);
  }, []);

  const handleChange = (value) => {
    setNewValue(value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    settingsService
      .updateSetting(settingKey, newValue)
      .then((setting) => {
        setCurrentValue(setting.setting);
        setNewValue(setting.setting);
        setDescription(setting.description);
        setLoading(false);
        showAlert("success", "Instelling opgeslagen");
      })
      .catch((error) => {
        setLoading(false);
        showAlert("error", error.message);
      });
  };

  if (loading) {
    return (
      <MKBox display="flex" justifyContent="center" alignItems="center" height="400px" width="100%">
        <CircularProgress size={40} />
      </MKBox>
    );
  }

  return (
    <MKBox>
      <Card sx={{ width: "100%", mt: 4 }}>
        <MKBox component="form" autoComplete="off" onSubmit={handleSubmit} p={3} width="100%">
          <Typography variant="h6">{settingKey}</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {description}
          </Typography>
          <MdTipTapDynamicHeight value={currentValue} onChange={handleChange} outputFormat="html" />
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <MKButton
                variant="contained"
                color="dark"
                type="submit"
                disabled={currentValue === newValue}
              >
                Opslaan
              </MKButton>
            </Grid>

            {alert && (
              <Grid item xs={12} md={6} display="flex" justifyContent="flex-end">
                <Fade in={!!alert}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {alert.severity === "error" ? (
                      <ErrorOutlineIcon color="error" fontSize="small" />
                    ) : (
                      <CheckCircleOutlineIcon color="success" fontSize="small" />
                    )}
                    <MKTypography
                      variant="body2"
                      color={alert.severity === "error" ? "error" : "success"}
                    >
                      {alert.message}
                    </MKTypography>
                  </Box>
                </Fade>
              </Grid>
            )}
          </Grid>
        </MKBox>
      </Card>
    </MKBox>
  );
}

export default SettingEditorInstance;

SettingEditorInstance.propTypes = {
  settingKey: PropTypes.string.isRequired,
};
